import React, { useState, useCallback, useEffect } from "react";
import Cropper from "react-easy-crop";
import { BrandButton } from "../assets";
import { readFile } from "../helpers";

export default function ImageField({
    currentImage,
    onChange,
    aspectRatio,
    AddButtonText,
    editable = true,
    maxSize = 1500000
}) {

    const [newFile, setNewFile] = useState(null);
    const [current, setCurrent] = useState(null);
    const [errorMsg, setErrorMsg] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

    useEffect(() => {
        setNewFile(null);
        setCroppedAreaPixels(null);
        (async function () {
            if (!currentImage) {
                setCurrent(null);
            } else if (currentImage instanceof Blob) {
                setCurrent(await readFile(currentImage));
            } else if (typeof currentImage === "string") {
                setCurrent(currentImage);
            } else if (typeof currentImage === "object") {
                setCurrent(currentImage.url);
            } else {
                setCurrent(null);
            }
        })();
    }, [currentImage]);

    useEffect(() => {
        let to;
        if (errorMsg !== null) {
            to = setTimeout(() => {
                setErrorMsg(null)
            }, 5000)
        }
        return () => {
            if (to) clearTimeout(to)
        }
    }, [errorMsg, setErrorMsg])

    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    return <div className="imageField">
        {!newFile && <>
            <div>
                <div className="overlay" />
                <div
                    className="currentImage"
                    style={current ? {
                        backgroundImage: `url("${current}")`
                    } : {}}
                />
            </div>
        </>}
        {newFile && <div className="crop-container" >
            {newFile && <Cropper
                // maxZoom={10}
                minZoom={0.1}
                zoomSpeed={0.1}
                restrictPosition={false}
                image={newFile.data}
                crop={crop}
                zoom={zoom}
                aspect={aspectRatio || 1}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
            />}
        </div>}
        {editable && <div className="flexy">
            {Boolean(currentImage) && <button
                className="brand-bg"
                onClick={e => {
                    onChange(null);
                }}
            >Remove</button>}
            <label className="button">
                {AddButtonText || (Boolean(currentImage) ? "Change" : "Select image")}
                <input
                    multiple={false}
                    type="file"
                    accept=".jpg,.jpeg,.gif,.png"
                    style={{ display: "none" }}
                    onChange={async e => {
                        if (!e.target.files || !e.target.files[0]) return;
                        const file = e.target.files[0];
                        // Check if file size exceeds maximum limit
                        if (file.size > maxSize) {
                            setErrorMsg(`Image cannot exceed maximum size of ${maxSize / 1000000}MB, please try different image.`);
                        } else {
                            e.target.value = null;
                            const data = await readFile(file);
                            setNewFile({ ...file, data });
                        }
                    }}
                />
            </label>
            {newFile && <BrandButton
                className="confirm"
                onClick={async e => {
                    // Construct a blob for the image.
                    const image = await getCroppedImg(newFile.data, croppedAreaPixels);
                    onChange(image);
                }}
            >
                Confirm
			</BrandButton>}
        </div>}
        <div className={`sizeError text-center ${errorMsg === null ? "hidden" : ""}`}>
            <div>{errorMsg}</div>
        </div>
    </div>

}

const createImage = url => new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', error => reject(error));
    image.src = url;
});

function getRadianAngle(degreeValue) {
    return (degreeValue * Math.PI) / 180
}

async function getCroppedImg(imageSrc, pixelCrop, rotation = 0) {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const safeArea = Math.max(image.width, image.height) * 2;

    // set each dimensions to double largest dimension to allow for a safe area for the
    // image to rotate in without being clipped by canvas context
    canvas.width = safeArea;
    canvas.height = safeArea;

    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // translate canvas context to a central location on image to allow rotating around the center.
    ctx.translate(safeArea / 2, safeArea / 2)
    ctx.rotate(getRadianAngle(rotation))
    ctx.translate(-safeArea / 2, -safeArea / 2)

    // draw rotated image and store data.
    ctx.drawImage(
        image,
        safeArea / 2 - image.width * 0.5,
        safeArea / 2 - image.height * 0.5
    )
    const data = ctx.getImageData(0, 0, safeArea, safeArea)

    // set canvas width to final desired crop size - this will clear existing context
    canvas.width = pixelCrop.width
    canvas.height = pixelCrop.height

    // paste generated rotate image with correct offsets for x,y crop values.
    ctx.putImageData(
        data,
        0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x,
        0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y
    )

    // As Base64 string
    // return canvas.toDataURL('image/jpeg');

    // As a blob
    return new Promise(resolve => {
        canvas.toBlob(resolve, 'image/jpeg');
    });
}